import React, { useEffect, useRef, useState } from 'react';
import { Radio, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { handleSetInitialValues, triggerNotification, useGetApiRequests } from '../../../../../../common/CommonExport';
import { useDispatch } from 'react-redux';
import { setProfileLoad } from '../../../../../../redux/slices/updateProfileSlice';
import { setEventData } from '../../../../../../redux/slices/empEventsSlice';
import Loader from '../../../../../../components/Loader/Loader';
import { useParams } from 'react-router-dom';
import { useIsMobile } from '../../../../../../hooks/commonHooks';
import { AssetDetail, AssetInfo, EmployeeDetails, RootState, NewAssetRow, EmployeePayload } from './EmployeAssets.types';
import { InitialValues, EmployeeInfo, CompanyDto } from '../edit-employee/types/EditEmployeeDetailsTypes';

const EmployeAssets = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const getAssetDetails = useGetApiRequests('getAssetDetails', 'GET');
  const updateEmployeeDetails = useGetApiRequests('companyEmployeeDetails', 'PUT');
  const employeeDetails: EmployeeDetails = useSelector((state: RootState) => state?.eventData?.particularEmployeeDetails as EmployeeDetails);



  const roleName = useSelector((state: RootState) => state?.loginUserData?.userData?.data?.role?.name);
  const [loader, setLoader] = useState<boolean>(false);
  const [assetDetails, setAssetDetails] = useState<AssetDetail[]>([]);
  const [companyDto, setCompanyDto] = useState<import("../edit-employee/types/EditEmployeeDetailsTypes").CompanyDto | Record<string, unknown>>({});
  const [initialValues, setInitialValues] = useState<InitialValues>();
  const [employeeImage, setEmployeeImage] = useState<string | null>(null);
  const [imageData, setImageData] = useState<FormData | string>(new FormData());
  const [configurationType, setConfigurationType] = useState(employeeDetails?.employeeAssertInfo?.assertType !== null ? employeeDetails?.employeeAssertInfo?.assertType : 'Basic');

  const [initialConfigType, setInitialConfigType] = useState<string>('Basic');
  const [assetId, setAssetId] = useState<string | number | null | undefined>();

  // Local asset list (seeded from redux, then extended locally)
  const [localAssetList, setLocalAssetList] = useState<AssetInfo[]>([]);
  const [isAssetDirty, setIsAssetDirty] = useState(false);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [newRow, setNewRow] = useState<NewAssetRow>({
    assertName: '',
    serialNumber: '',
    isHandled: false,
  });

  // Ref for the add row — used to detect outside clicks
  const addRowRef = useRef<HTMLTableRowElement>(null);
  // Guard against double-commit (onBlur + onChange can both fire in same interaction)
  const isCommittingRef = useRef(false);

  const commitNewRow = (row: NewAssetRow) => {
    if (isCommittingRef.current) return; // already committed this cycle
    isCommittingRef.current = true;
    setLocalAssetList((prev: AssetInfo[]) => [...prev, { assertName: row.assertName, serialNumber: row.serialNumber, isHandled: row.isHandled }]);
    setIsAssetDirty(true);
    setIsAddingRow(false);
    setNewRow({ assertName: '', serialNumber: '', isHandled: false });
    setTimeout(() => { isCommittingRef.current = false; }, 0); // reset after tick
  };

  // Auto-dismiss the add row when user clicks outside it without filling fields
  useEffect(() => {
    if (!isAddingRow) return;
    const handleClickOutside = (e: MouseEvent) => {
      // Ant Design Select/dropdown portals render outside the DOM tree — ignore those clicks
      const isInsideAntDropdown = !!(e.target as HTMLElement)?.closest('.ant-select-dropdown, .ant-picker-dropdown');
      if (isInsideAntDropdown) return;

      if (addRowRef.current && !addRowRef.current.contains(e.target as Node)) {
        // Only dismiss if row is still empty (not yet committed)
        if (!newRow.assertName || !newRow.serialNumber?.trim()) {
          setIsAddingRow(false);
          setNewRow({ assertName: '', serialNumber: '', isHandled: false });
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAddingRow, newRow]);

  // Seed local list from redux whenever employeeDetails changes
  useEffect(() => {
    setLocalAssetList(employeeDetails?.assetInfoList ?? []);
  }, [employeeDetails?.assetInfoList]);
  const getConfigurationIdAPI = async () => {
    try {
      const res = await getAssetDetails('') as { data?: { data?: AssetDetail[] } };
      if (res?.data?.data) setAssetDetails(res.data.data);
    } catch (err) {
    }
  };

  useEffect(() => {
    getConfigurationIdAPI();
    const configType = employeeDetails?.employeeAssertInfo?.assertType ?? 'Basic';
    setConfigurationType(configType);
    setInitialConfigType(configType);
  }, []);
  useEffect(() => {
    handleSetInitialValues(
      employeeDetails as unknown as EmployeeInfo,
      setEmployeeImage,
      (values: InitialValues) => setInitialValues(values),
      (dto: CompanyDto) => setCompanyDto(dto)
    );
  }, [employeeDetails]);
  function findChangedData(obj1: Record<string, unknown>, obj2: Record<string, unknown> | undefined) {
    const changedData: Record<string, unknown> = {};
    for (const key in obj1) {
      if (obj1[key] !== obj2?.[key]) {
        changedData[key] = obj2?.[key];
      }
    }
    return changedData;
  }
  const onSubmit = () => {
    const changedData = findChangedData(companyDto as Record<string, unknown>, initialValues?.companyDto as unknown as Record<string, unknown>);

    const originalList: AssetInfo[] = employeeDetails?.assetInfoList ?? [];

    // Build assetInfoList with only changed or newly added items
    const assetInfoList = localAssetList
      .filter((asset: AssetInfo) => {
        const matched = assetDetails.find((a: AssetDetail) => a.assertType === asset.assertName);
        const assertId = matched?.assertId ?? asset.assertId ?? null;
        // Include if newly added (no assertId from API)
        if (!assertId) return true;
        // Include if isHandled changed from original
        const original = originalList.find((o: AssetInfo) => o.assertId === assertId);
        return !original || original.isHandled !== asset.isHandled;
      })
      .map((asset: AssetInfo) => {
        const matched = assetDetails.find((a: AssetDetail) => a.assertType === asset.assertName);
        return {
          assertId: matched?.assertId ?? asset.assertId ?? null,
          serialNumber: asset.serialNumber,
          isHandled: asset.isHandled,
        };
      });

    const mainPayload: EmployeePayload = {
      employeeDto: (initialValues?.employeeDto as unknown as Record<string, unknown>) || {},
      companyDto: changedData,
      assertId: assetId,
      assetInfoList,
    };
    if (mainPayload.employeeDto) {
      if (imageData === '') {
        mainPayload.employeeDto.profileUrl = null;
      } else {
        mainPayload.employeeDto.profileUrl = employeeImage;
      }
    }

    setLoader(true);
    const headers = { 'Content-Type': 'multipart/form-data' };
    updateEmployeeDetails(imageData === '' ? {} : imageData, { employeeInformationDto: mainPayload }, { id: id }, headers)
      .then((res) => {
        dispatch(setProfileLoad());
        const responseData = res as { data?: { status?: { message?: string } } };
        triggerNotification('success', '', responseData?.data?.status?.message ?? 'Success', 'topRight');
        dispatch(setEventData({}));
        setInitialConfigType(configurationType ?? 'Basic');
        setIsAssetDirty(false);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        const errorData = err as { response?: { data?: { message?: string; status?: { message?: string } } } };
        triggerNotification('error', '', errorData?.response?.data?.message ?? errorData?.response?.data?.status?.message ?? 'An error occurred', 'topRight');
      })
      .finally(() => {
        setLoader(false);
      });
  };


  const assetConfig = [
    { "label": "Basic", value: 'Basic' },
    { "label": "Intermediate", value: 'Intermediate' },
    { "label": "Advanced", value: 'Advanced' }
  ];
  const assetType = [
    { "label": "Laptop", value: 'Laptop' },
    { "label": "Desktop", value: 'Desktop' },
  ];

  const handleChange = (value: string) => {
    const selectedAsset = assetDetails?.find((asset: AssetDetail) => asset?.assertType === value);
    const assertId = selectedAsset ? selectedAsset?.assertId : null;
    setAssetId(assertId);
    setConfigurationType(value);
  };

  const selectedAssetDetails = assetDetails?.find((asset: AssetDetail) => asset?.assertType === configurationType);
  return (
    <div className='salary_details font-rubik flex flex-col' style={{ height: '100%' }}>
      {loader && <Loader />}
      <div className="employee-assets mt-5 flex-1 overflow-y-auto pb-4">
        <div className="configuration-section mb-8 2xl:p-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">Configuration Details</h3>

          <div className="flex flex-col space-y-6">
            <div className="flex items-center label-row">
              <div className="w-48 flex-shrink-0">
                <label className='font-rubik text-gray-700 font-medium'>Configuration Type</label>
              </div>
              <div className="flex-1 ">
                <Select
                  disabled={isMobile ? true : !(roleName === "IT Admin" || roleName === "HR Admin")}
                  className='font-rubik w-full max-w-xs'
                  value={configurationType}
                  onChange={handleChange}
                  options={assetConfig.map(option => ({ label: option.label, value: option.value }))}
                />
              </div>
            </div>


            {selectedAssetDetails && (
              <div className="bg-gray-50 rounded-md p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <label className='font-rubik text-xs sm:text-sm text-gray-500 uppercase tracking-wider'>
                      Processor
                    </label>

                    <p className='font-rubik text-gray-800 font-medium'>
                      {selectedAssetDetails?.processor || '-'}
                    </p>
                  </div>


                  <div className="space-y-1">
                    <label className='font-rubik text-xs sm:text-sm text-gray-500 uppercase tracking-wider'>RAM</label>
                    <p className='font-rubik text-gray-800 font-medium'>
                      {selectedAssetDetails?.ram || '-'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className='font-rubik text-xs sm:text-sm text-gray-500 uppercase tracking-wider'>SSD</label>
                    <p className='font-rubik text-gray-800 font-medium'>
                      {selectedAssetDetails?.ssd || '-'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className='font-rubik text-xs sm:text-sm text-gray-500 uppercase tracking-wider'>Graphics Card</label>
                    <p className='font-rubik text-gray-800 font-medium'>
                      {selectedAssetDetails?.graphicCard || '-'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className='font-rubik text-xs sm:text-sm text-gray-500 uppercase tracking-wider'>OS</label>
                    <p className='font-rubik text-gray-800 font-medium'>
                      {selectedAssetDetails?.os || '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 1: Device Information */}
        <div className="device-section device-type-box p-6 bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">Device Information</h3>
          <div className="flex flex-col space-y-5">
            <div className="flex items-center label-row">
              <div className="w-40 flex-shrink-0 label-box">
                <label className='font-rubik text-gray-700 font-medium'>Device Type</label>
              </div>
              <div>
                <Radio.Group
                  className='font-rubik'
                  name="deviceType"
                  disabled={true}
                  value={employeeDetails?.deviceType !== null ? employeeDetails?.deviceType : 'Laptop'}
                  options={assetType}
                />
              </div>
            </div>
            <div className="flex items-center label-row">
              <div className="w-40 flex-shrink-0 label-box">
                <label className='font-rubik text-gray-700 font-medium'>Other Peripherals</label>
              </div>
              <p className='font-rubik text-gray-800 font-medium'>
                {employeeDetails?.otherPeripherals !== "" ? employeeDetails?.otherPeripherals : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Card 2: External Assets */}
        {((roleName === "IT Admin" || roleName === "HR Admin") || localAssetList.length > 0) && (
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">External Assets</h3>
              {(roleName === "IT Admin" || roleName === "HR Admin") && !isAddingRow && (
                <button
                  onClick={() => setIsAddingRow(true)}
                  title="Add External Asset"
                  className="flex items-center justify-center w-8 h-8 rounded-full text-white bg-[#E4157E] hover:bg-pink-700 transition-colors shadow-sm white-icon-btn"
                >
                  <PlusOutlined style={{ fontSize: '14px' }} />
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px', tableLayout: 'fixed' }}>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className='font-rubik text-sm text-gray-500 font-medium text-left pb-3 w-[20%]'>Name</th>
                    <th className='font-rubik text-sm text-gray-500 font-medium text-left pb-3 w-[20%]'>Serial No</th>
                    <th className='font-rubik text-sm text-gray-500 font-medium text-left pb-3 w-[30%]'>Status</th>
                    <th className='font-rubik text-sm text-gray-500 font-medium text-left pb-3 w-[15%]'>Date of Assign</th>
                    <th className='font-rubik text-sm text-gray-500 font-medium text-left pb-3 w-[15%]'>Date of Return</th>
                  </tr>
                </thead>
                <tbody>
                  {localAssetList.map((asset: AssetInfo, index: number) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className='font-rubik text-gray-800 font-medium py-3 pr-4 truncate'>{asset.assertName || '-'}</td>
                      <td className='font-rubik text-gray-800 font-medium py-3 pr-4 truncate'>{asset.serialNumber || '-'}</td>
                      <td className='py-3 pr-4'>
                        {(roleName === "IT Admin" || roleName === "HR Admin") ? (() => {
                          const originalItem = employeeDetails?.assetInfoList?.find((o: AssetInfo) => o.assertId === asset.assertId);
                          const isOriginallyReturned = originalItem?.isHandled === true;
                          return (
                            <Radio.Group
                              value={asset.isHandled}
                              onChange={(e) => {
                                setLocalAssetList((prev: AssetInfo[]) => prev.map((item, i) => i === index ? { ...item, isHandled: e.target.value } : item));
                                setIsAssetDirty(true);
                              }}
                            >
                              <Radio value={false} disabled={isOriginallyReturned}><span className='font-rubik text-gray-800'>Assigned</span></Radio>
                              <Radio value={true}><span className='font-rubik text-gray-800'>Returned</span></Radio>
                            </Radio.Group>
                          );
                        })() : (
                          <Radio.Group disabled value={asset.isHandled}>
                            <Radio value={false}><span className='font-rubik text-gray-800'>Assigned</span></Radio>
                            <Radio value={true}><span className='font-rubik text-gray-800'>Returned</span></Radio>
                          </Radio.Group>
                        )}
                      </td>
                      <td className='font-rubik text-gray-800 font-medium py-3 pr-4 whitespace-nowrap'>
                        {asset.dateOfGet ? new Date(asset.dateOfGet).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className='font-rubik text-gray-800 font-medium py-3 pr-4 whitespace-nowrap'>
                        {asset.dateOfHandle ? new Date(asset.dateOfHandle).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                    </tr>
                  ))}

                  {/* Inline add row — auto-commits when Name + Serial No are filled, auto-dismisses on outside click */}
                  {isAddingRow && (
                    <tr ref={addRowRef} className="border-b border-blue-100 bg-blue-50">
                      <td className='py-2 pr-4'>
                        <Select
                          className='font-rubik w-36 asset-name-select'
                          placeholder="Select asset"
                          popupClassName="asset-name-select-dropdown"
                          popupMatchSelectWidth={false}
                          value={newRow.assertName || undefined}
                          onChange={(val) => {
                            const updated = { ...newRow, assertName: val };
                            setNewRow(updated);

                            if (updated.serialNumber?.trim()) {
                              commitNewRow(updated);
                            }
                          }}
                          options={assetDetails
                            .filter((a: AssetDetail) => {

                              const existing = localAssetList.find(
                                (item: AssetInfo) => (item.assertName === a.assertType) || (item.assertId === a.assertId)
                              );

                              return !existing || existing.isHandled === true;
                            })
                            .map((a: AssetDetail) => ({ label: a.assertType, value: a.assertType }))}
                        />
                      </td>
                      <td className='py-2 pr-4'>
                        <input
                          type='text'
                          placeholder='Serial No'
                          value={newRow.serialNumber}
                          onChange={(e) => setNewRow((r: NewAssetRow) => ({ ...r, serialNumber: e.target.value }))}
                          onBlur={() => {
                            // auto-commit when serial no loses focus and name is selected
                            if (newRow.assertName && newRow.serialNumber?.trim()) {
                              commitNewRow(newRow);
                            }
                          }}
                          className='font-rubik border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 w-28 focus:outline-none focus:border-pink-400'
                        />
                      </td>
                      <td className='py-2 pr-4'>
                        <Radio.Group
                          value={newRow.isHandled}
                          onChange={(e) => setNewRow((r: NewAssetRow) => ({ ...r, isHandled: e.target.value }))}
                        >
                          <Radio value={false}><span className='font-rubik text-sm text-gray-800'>Assigned</span></Radio>
                          <Radio disabled value={true}><span className='font-rubik text-sm text-gray-800'>Returned</span></Radio>
                        </Radio.Group>
                      </td>
                      {/* Date of Get & Date of Handle — managed by API */}
                      <td className='font-rubik text-gray-400 text-sm py-2 pr-4'>—</td>
                      <td className='font-rubik text-gray-400 text-sm py-2 pr-4'>—</td>
                    </tr>
                  )}

                  {/* Empty state */}
                  {localAssetList.length === 0 && !isAddingRow && (
                    <tr>
                      <td colSpan={6} className='text-center py-6 font-rubik text-gray-400 text-sm'>No external assets added yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="overflow-x-auto" style={{ display: 'none' }}></div>
      </div>


      {!isMobile && (roleName === "IT Admin" || roleName === "HR Admin") && (
        <div className='flex justify-end py-3 px-2 border-t border-gray-200 bg-white'>
          <button
            className={`addButton mr-4 primary  ${(configurationType === initialConfigType && !isAssetDirty) ? "disabled" : ""}`}
            type="submit"
            onClick={onSubmit}
            disabled={configurationType === initialConfigType && !isAssetDirty}
          >
            Update Configuration
          </button>
        </div>
      )}
    </div>
  );
}

export default EmployeAssets;
